import React, {useEffect, useState} from "react";
import {Typography, Button, Box} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import Grid from '@mui/material/Grid';
import {green, red} from "@mui/material/colors";

import CustomAlert from "../CustomAlert";
import {
    GET_ASSESSMENTS,
    ASSESSMENT_TAGS,
    SUBMIT_ASSESSMENT,
    LOOKUP_FILTERED_BOOKINGS
} from "../../queries/GraphQLQueries";
import {FIRST_ASSESSMENT_INDEX} from "../../helpers/AssessmentUtils";
import TagSelectDropdown from "../assessment/TagSelectDropdown";
import AssessmentToggle from "../assessment/AssessmentToggle";

const styles = {
    container: {
        grid: (compact) => ({
            flexDirection: compact ? "column" : "row",
            justifyContent: compact ? "flex-start" : "space-between",
            alignItems: compact ? "stretch" : "flex-start",
            minHeight: "5rem",
            marginTop: compact ? "-1rem" : "0",
            backgroundColor: "white",
            padding: compact ? "0" : "0.5rem"
        }),
        compact: {
            display: "flex",
            flexDirection: "column",
            alignItems: "space-between",
            gap: "0.5rem",
            marginX: "0.5rem"
        }
    },
    grid: {
        buttonItem: (compact) => ({
            alignSelf: compact ? 'end' : 'flex-start'
        }),
        dropdownItem: {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            marginLeft: "-0.5rem",
        },
        horizontalRow: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between"
        },
    },
    typography: {
        title: {
            fontWeight: "bold",
            margin: "-0.5rem 0 -0.5rem 0"
        }
    },
    button: {
        submit: (isAssessmentPassed, compact) => ({
            margin: compact ? "0" : "0.5em 0.7rem 0 0",
            marginRight: compact ? "0" : "0.7rem",
            backgroundColor: isAssessmentPassed ? green[700] : red[700],
            alignSelf: "auto"
        })
    }
};

export default function ChildAssessmentStrip({bookingId, onAssessmentSubmitted, compact = false}) {

    const {data} = useQuery(GET_ASSESSMENTS, {variables: {bookingIds: [bookingId]}});
    const {data: tagsData} = useQuery(ASSESSMENT_TAGS);
    const [submitAssessment] = useMutation(SUBMIT_ASSESSMENT, {
        refetchQueries: [
          {query: GET_ASSESSMENTS, variables: {bookingIds: [bookingId]}},
          {query: LOOKUP_FILTERED_BOOKINGS}, 'GetFilteredBookings'
        ]
    });

    const [isAssessmentPassed, setIsAssessmentPassed] = useState(true);
    const [availableTags, setAvailableTags] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [isAssessmentDirty, setIsAssessmentDirty] = useState(false);

    useEffect(() => {
        if (!tagsData?.tags) {
            return;
        }

        const tags = tagsData.tags.map(tag => ({id: tag.id, name: tag.name}));
        setAvailableTags(tags);

    }, [tagsData]);

    useEffect(() => {

        if (!data?.assessments) {
            return;
        }

        const assessment = data.assessments[FIRST_ASSESSMENT_INDEX];

        if (assessment) {
            setIsAssessmentPassed(assessment.pass);
            setIsAssessmentDirty(false);

            const ids = assessment.tags?.map(tag => tag.id) || [];
            setSelectedTagIds(ids);
        } else {
            setIsAssessmentDirty(true);
            setIsAssessmentPassed(true);
            setSelectedTagIds([]);
        }
    }, [data, bookingId]);

    const handleSubmit = () => {
        setAlertMessage({children: 'Submitting Assessment...', severity: 'info'});
        submitAssessment({
            variables: {
                bookingId,
                pass: isAssessmentPassed,
                tags: selectedTagIds
            }
        }).then(() => {
            setAlertMessage({children: 'Assessment submitted', severity: 'success'});
            setIsAssessmentDirty(false);

            if (onAssessmentSubmitted) {
                onAssessmentSubmitted();
            }
        }).catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.log("error " + error)
        })
    };

    const handleCloseCustomAlert = () => {
        setAlertMessage(null);
    };

    const handlePassChange = (event) => {
        const isPassed = event.target.checked;
        setIsAssessmentPassed(isPassed);
        setIsAssessmentDirty(true);
    };

    const handleNewTagSelection = (event) => {
        const newSelectedTagIds = event.target.value;
        setSelectedTagIds(newSelectedTagIds);
        setIsAssessmentDirty(true);
    };

    const handleRemoveTag = (id) => {
        const newIds = selectedTagIds.filter(tagId => tagId !== id);
        setSelectedTagIds(newIds);
        setIsAssessmentDirty(true);
    };

    if (compact) {
        return <>
            <Box sx={styles.container.compact}>
                <Box sx={styles.grid.horizontalRow}>
                    <Box marginTop="0.8rem">
                        <Typography variant="subtitle2" sx={styles.typography.title}>Assessment Result</Typography>
                        <AssessmentToggle isAssessmentPassed={isAssessmentPassed} onChange={handlePassChange}/>
                    </Box>
                    <TagSelectDropdown
                        selectedTagIds={selectedTagIds}
                        availableTags={availableTags}
                        handleNewTagSelection={handleNewTagSelection}
                        handleRemoveTag={handleRemoveTag}/>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={styles.button.submit(isAssessmentPassed, compact)}
                        disabled={!isAssessmentDirty}>
                        Submit
                    </Button>
                </Box>
            </Box>
            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
        </>
    }

    return <>
        <Grid container sx={styles.container.grid(compact)}>
            <Grid item>
                <Typography variant="subtitle1" fontWeight="bold">Assessment Result</Typography>
                <AssessmentToggle isAssessmentPassed={isAssessmentPassed} onChange={handlePassChange}/>
            </Grid>

            <Grid item sx={styles.grid.dropdownItem}>
                <TagSelectDropdown
                  selectedTagIds={selectedTagIds}
                  availableTags={availableTags}
                  handleNewTagSelection={handleNewTagSelection}
                  handleRemoveTag={handleRemoveTag}/>
            </Grid>

            <Grid item sx={styles.grid.buttonItem(compact)}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={styles.button.submit(isAssessmentPassed, compact)}
                    disabled={!isAssessmentDirty}>
                    Submit
                </Button>
            </Grid>
        </Grid>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
    </>
}